<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="page-title-box">
          <div class="d-flex justify-content-between align-items-center">
            <h4 class="page-title">Self Assessment Report</h4>

            <div>
              <button class="ms-3 btn btn-danger btn-sm" @click="exportPdfClick">
                <span v-if="pdfLoading" class="spinner-border spinner-border-sm"></span>
                {{ pdfLoading ? "Loading..." : "Export PDF" }}
              </button>

              <button class="ms-2 btn btn-success btn-sm" @click="exportExcelClick">
                <span v-if="excelLoading" class="spinner-border spinner-border-sm"></span>
                {{ excelLoading ? "Loading..." : "Export Excel" }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- FILTER SECTION -->
    <div class="card">
      <div class="card-body">

        <div class="d-flex flex-wrap align-items-center">

          <!-- From Date -->
          <div class="me-3">
            <label class="col-form-label me-1">Training Date</label>
            <div style="width: 180px">
              <flat-pickr
                v-model="from_date"
                class="form-control"
                @change="fetchData"
              />
            </div>
          </div>

          <!-- Module -->
          <div class="me-3">
            <label class="col-form-label me-1">Module</label>
            <div style="width: 220px">
              <v-select
                v-model="module"
                :options="modules"
                label="name"
                :clearable="true"
                @option:selected="fetchData"
              />
            </div>
          </div>

          <!-- Trainer -->
          <div class="me-3">
            <label class="col-form-label me-1">Trainer</label>
            <div style="width: 200px">
              <v-select
                v-model="trainer"
                :options="trainers"
                label="name"
                :clearable="true"
                @option:selected="fetchData"
              />
            </div>
          </div>

          <div @click="refreshData" class="icon-css pt-4 ms-2">
            <i style="color: #4a81d4" class="fe-rotate-cw"></i>
          </div>

        </div>

        <!-- SEARCH -->
        <div class="d-flex align-items-center flex-row-reverse mt-3">
          <input
            type="text"
            class="form-control ms-2"
            style="width: 220px"
            v-model="searchQuery"
            placeholder="Search..."
          />
          Search :
        </div>

        <!-- TABLE -->
        <div class="table-responsive mt-4">
          <DataTable2
            v-if="!loadingTable"
            :data="tableData"
            :columns="columns"
            :table="title"
            :loading="loadingTable"
            :totalRecords="totalRecords"
            :currentPage="currentPage"
            :pageSize="pageSize"
            @update:pagination="handlePagination"
          />
        </div>

      </div>
    </div>
  </div>
</template>
<script>
import axios from "axios";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import DataTable2 from "@/components/shared/DataTable2.vue";
import { exportExcel } from "@/utlis/excel-export-already-formated-utlis";
import { exportPdf } from "@/utlis/pdf-export-already-formated-utlis";

export default {
  components: { vSelect, flatPickr, DataTable2 },

  data() {
    return {
      baseUrl: process.env.VUE_APP_BASE_URL,

      modules: [],
      trainers: [],

      module: null,
      trainer: null,
      from_date: null,
      searchQuery: "",

      title: "Self Assessment Report",

      tableData: [],
      columns: [
        { data: "employee_name", title: "Employee Name" },
        { data: "employee_id", title: "Employee ID" },
        { data: "business_unit", title: "Business Unit" },
        { data: "department_name", title: "Department" },
        { data: "module_name", title: "Module" },
        { data: "trainer_names", title: "Trainer" },
        { data: "training_date", title: "Training Date" },
        { data: "total_score", title: "Total Score" },
      ],

      totalRecords: 0,
      currentPage: 1,
      pageSize: 10,

      loadingTable: false,
      pdfLoading: false,
      excelLoading: false,

      searchTimeout: null,
    };
  },

  watch: {
    searchQuery() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.fetchData();
      }, 800);
    },
  },

  methods: {

    async getModules() {
      const res = await axios.get(`${this.baseUrl}admin/v3/module-list`);
      this.modules = res.data.data || [];
    },

    async getTrainers() {
      const res = await axios.get(`${this.baseUrl}admin/v3/trainer-list`);
      this.trainers = res.data.data || [];
    },

    async fetchTableData({ currentPage, pageSize }) {
      this.loadingTable = true;

      try {
        const res = await axios.get(
          `${this.baseUrl}admin/v3/reports/self-assessment-results`,
          {
            params: {
              page: currentPage,
              per_page: pageSize,
              module_id: this.module?.id || "",
              trainer_id: this.trainer?.employee_id || "",
              from_date: this.from_date || "",
              search: this.searchQuery || "",
            },
          }
        );
        console.log("Fetched table data:", res.data.data);

        this.tableData = res.data.data;
        this.totalRecords = res.data.total || res.data.data.length;
        this.currentPage = currentPage;
        this.pageSize = pageSize;

      } finally {
        this.loadingTable = false;
      }
    },

    fetchData() {
      this.currentPage = 1;
      this.pageSize = 10;
      this.fetchTableData({
        currentPage: this.currentPage,
        pageSize: this.pageSize,
      });
    },

    handlePagination({ currentPage, pageSize }) {
      this.fetchTableData({ currentPage, pageSize });
    },

    async getWithoutPaginate() {
      const res = await axios.get(
        `${this.baseUrl}admin/v3/reports/self-assessment-results`,
        {
          params: {
            module_id: this.module?.id || "",
            trainer_id: this.trainer?.employee_id || "",
            from_date: this.from_date || "",
            search: this.searchQuery || "",
          },
        }
      );
      return res.data.data || [];
    },

    async exportPdfClick() {
      const data = await this.getWithoutPaginate();
      if (data.length) {
        this.pdfLoading = true;
        exportPdf(data, this.columns, this.title);
        this.pdfLoading = false;
      }
    },

    async exportExcelClick() {
      const data = await this.getWithoutPaginate();
      if (data.length) {
        this.excelLoading = true;
        exportExcel(data, this.columns, this.title);
        this.excelLoading = false;
      }
    },

    refreshData() {
      this.module = null;
      this.trainer = null;
      this.from_date = null;
      this.searchQuery = "";
      this.fetchData();
    },
  },

  async created() {
    await this.getModules();
    await this.getTrainers();
    await this.fetchData();
  },
};
</script>

<style scoped>
.page-item.active .page-link {
  z-index: 3;
  color: #fff;
  background-color: #4a81d4;
  border-color: #4a81d4;
}
</style>